import { DotsHorizontal, Download01, FilterLines } from "@untitledui/icons";
import { type FC, type ReactNode, useState } from "react";
import {
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from "react-aria-components";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

export interface TableActionsProps {
	onFilterClick?: () => void;
	onViewOptionsClick?: () => void;
	onExportClick?: (visibleColumns?: string[]) => void;
	/** Whether the filter drawer is currently open */
	isFilterOpen?: boolean;
	/** Whether any non-search filters are currently applied */
	hasActiveFilters?: boolean;
	/** Whether the search term is non-empty */
	hasActiveSearch?: boolean;
	/** Whether export is currently in progress */
	isExportLoading?: boolean;
	/**
	 * Manage-columns panel (TanStack visibility / order).
	 * Rendered under the action items in the options menu.
	 */
	viewOptionsContent?: ReactNode | ((close: () => void) => ReactNode);
	/** Visible column IDs to pass to export callback */
	visibleColumns?: string[];
	/** Total row count for the table */
	rowCount?: number;
	/** Show Hide/Show Filters in the options menu. @default true */
	showFilterButton?: boolean;
	/** Show Download in the options menu. @default true */
	showExportButton?: boolean;
	/**
	 * Layout variant.
	 * - `toolbar` — offsets above the table card
	 * - `inline` — TableFilterBar (Workspace) without negative margins
	 * - `header` — PageHeader overflow; IconButton trigger matches header chrome
	 * @default "toolbar"
	 */
	variant?: "toolbar" | "inline" | "header";
	/** Controlled options menu open state */
	isViewOptionsOpen?: boolean;
	/** Called when the options menu open state changes */
	onViewOptionsOpenChange?: (isOpen: boolean) => void;
}

interface OptionsMenuActionProps {
	label: string;
	icon: FC<{ className?: string }>;
	onAction: () => void;
	isDisabled?: boolean;
	testId?: string;
}

/**
 * Action row styled like Dropdown.Item for use inside the options Dialog.
 */
function OptionsMenuAction({
	label,
	icon: Icon,
	onAction,
	isDisabled = false,
	testId,
}: OptionsMenuActionProps) {
	return (
		<button
			type="button"
			data-testid={testId}
			disabled={isDisabled}
			onClick={onAction}
			className={cx(
				// Figma MenuItem Content — px-lg py-md, icon 20px + gap-md
				"group flex w-full cursor-pointer items-center gap-(--spacing-md,8px) rounded-md px-(--spacing-lg,12px) py-(--spacing-md,8px) outline-focus-ring transition duration-100 ease-linear",
				"hover:bg-primary_hover focus-visible:outline-2 focus-visible:-outline-offset-2",
				isDisabled && "cursor-not-allowed opacity-50",
			)}
		>
			<Icon
				aria-hidden="true"
				className={cx(
					"size-5 shrink-0 stroke-[2.25px]",
					isDisabled ? "text-fg-disabled" : "text-fg-quaternary",
				)}
			/>
			<span
				className={cx(
					"grow truncate text-left text-sm font-semibold",
					isDisabled ? "text-disabled" : "text-secondary",
				)}
			>
				{label}
			</span>
		</button>
	);
}

/**
 * Table Options control (Figma `_TableOptions` 3930:53177).
 * ⋯ trigger opens a menu with Download, Hide/Show Filters, and Manage Columns
 * (TanStack column visibility + order via `viewOptionsContent`).
 */
export function TableActions({
	onFilterClick,
	onViewOptionsClick,
	onExportClick,
	isFilterOpen = false,
	isExportLoading = false,
	viewOptionsContent,
	visibleColumns,
	rowCount,
	showFilterButton = true,
	showExportButton = true,
	variant = "toolbar",
	isViewOptionsOpen: controlledViewOptionsOpen,
	onViewOptionsOpenChange,
}: TableActionsProps) {
	const { t } = useAppTranslation();
	const [uncontrolledViewOptionsOpen, setUncontrolledViewOptionsOpen] =
		useState(false);
	const isViewOptionsOpen =
		controlledViewOptionsOpen ?? uncontrolledViewOptionsOpen;

	const handleViewOptionsOpenChange = (isOpen: boolean) => {
		if (controlledViewOptionsOpen === undefined) {
			setUncontrolledViewOptionsOpen(isOpen);
		}
		onViewOptionsOpenChange?.(isOpen);
		if (isOpen) {
			onViewOptionsClick?.();
		}
	};

	const isExportUnavailable = Boolean(rowCount && rowCount > 10000);
	const isExportDisabled = isExportLoading || isExportUnavailable;

	const optionsLabel = t("common.tableActions.options");
	const downloadLabel = t("common.tableActions.download");
	const filterLabel = isFilterOpen
		? t("common.tableActions.hideFilter")
		: t("common.tableActions.showFilter");

	const hasActionItems = showExportButton || showFilterButton;
	/** Manage columns panel should include `Dropdown.SectionHeader` (order=below) for the separator. */
	const hasManageColumns = viewOptionsContent != null;

	const triggerButton =
		variant === "header" ? (
			<IconButton
				data-testid="table-actions-options-btn"
				aria-label={optionsLabel}
				hierarchy="secondary"
				size="sm"
				shape="square"
				icon={DotsHorizontal}
			/>
		) : (
			<Button
				data-testid="table-actions-options-btn"
				aria-label={optionsLabel}
				size="sm"
				color={isViewOptionsOpen ? "secondary-selected" : "secondary"}
				iconLeading={DotsHorizontal}
			/>
		);

	const trigger = (
		<Tooltip
			title={optionsLabel}
			placement={variant === "header" ? "left" : "top"}
			arrow={variant === "header" || variant === "inline"}
			isDisabled={isViewOptionsOpen}
		>
			{triggerButton}
		</Tooltip>
	);

	return (
		<div
			className={cx(
				"flex items-center",
				variant === "toolbar" && "mr-6 mt-[-30px] mb-3",
			)}
			data-testid="table-actions"
		>
			<AriaDialogTrigger
				isOpen={isViewOptionsOpen}
				onOpenChange={handleViewOptionsOpenChange}
			>
				{trigger}
				<AriaPopover
					placement="bottom end"
					offset={8}
					className={({ isEntering, isExiting }) =>
						cx(
							"origin-(--trigger-anchor-point) will-change-transform z-50",
							isEntering &&
								"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
							isExiting &&
								"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
						)
					}
				>
					<AriaDialog
						className="w-[min(100vw-2rem,346px)] overflow-hidden rounded-2xl bg-primary p-(--spacing-xl,16px) shadow-lg ring ring-secondary_alt focus:outline-hidden"
						data-testid="table-actions-options-menu"
					>
						{({ close }) => (
							<div className="flex flex-col">
								{hasActionItems ? (
									<div className="flex flex-col gap-(--spacing-none,0px)">
										{showExportButton ? (
											<Tooltip
												title={t("common.tableActions.exportNotAvailable")}
												isDisabled={!isExportUnavailable}
											>
												<OptionsMenuAction
													testId="table-actions-download-btn"
													label={downloadLabel}
													icon={Download01}
													isDisabled={isExportDisabled}
													onAction={() => {
														onExportClick?.(visibleColumns);
														close();
													}}
												/>
											</Tooltip>
										) : null}
										{showFilterButton ? (
											<OptionsMenuAction
												testId="table-actions-filter-btn"
												label={filterLabel}
												icon={FilterLines}
												onAction={() => {
													onFilterClick?.();
													close();
												}}
											/>
										) : null}
									</div>
								) : null}

								{/* Separator lives on Dropdown.SectionHeader order="below" inside viewOptionsContent */}
								{hasManageColumns
									? typeof viewOptionsContent === "function"
										? viewOptionsContent(close)
										: viewOptionsContent
									: null}
							</div>
						)}
					</AriaDialog>
				</AriaPopover>
			</AriaDialogTrigger>
		</div>
	);
}
